import { useNavigate, useSearchParams } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useMutation } from '@tanstack/react-query';
import { AutocompleteResTypeData, getAutocomplete } from '../services/api';
import { buildSearchParams } from '../utils/search.ts';

export function useAutocomplete() {
  const [searchParams] = useSearchParams();
  const params = searchParams.get('query');
  // Getting selected value from query params if they exist to populate the search bar
  const [selectedValue, setValue] = useState(
    params ? (JSON.parse(params) as AutocompleteResTypeData) : undefined
  );
  const [_inputValue, setInputValue] = useState(null as string | null);

  const defaultInputValue = useMemo(() => {
    if (params) {
      const paramsOBJ = JSON.parse(params) as AutocompleteResTypeData;
      return paramsOBJ.address
        ? paramsOBJ.address
        : (paramsOBJ.city || paramsOBJ.county) && paramsOBJ.state
          ? (paramsOBJ.city ? paramsOBJ.city : paramsOBJ.county || '') +
              ', ' +
              paramsOBJ.state || ''
          : '';
    }
    return null;
  }, [params]);

  const inputValue = _inputValue ?? defaultInputValue ?? '';

  const navigate = useNavigate();
  const {
    mutate,
    data: autoCompleteResults,
    isPending,
    reset,
    error,
  } = useMutation({
    mutationKey: ['autocomplete'],
    mutationFn: async (query: string) => {
      return getAutocomplete({ body: { search: query } });
    },
  });

  useEffect(() => {
    if (!error) return;
    console.error(error);
  }, [error]);

  const submitSearch = (value?: AutocompleteResTypeData) => {
    if (isPending || !autoCompleteResults) return;
    if (!value) value = selectedValue;
    if (value) {
      const { searchType, title, ...newValue } = value;
      const queryParams = buildSearchParams(newValue);
      void navigate(`/search?${queryParams}`);
    }
  };

  const search = useMemo(
    () =>
      debounce((value: string) => {
        if (value.length > 2) {
          mutate(value);
        } else {
          reset();
        }
      }, 300),
    [mutate, reset]
  );

  return {
    search,
    submitSearch,
    inputValue,
    selectedValue,
    setValue,
    setInputValue,
    autoCompleteResults,
    isPending,
  };
}
